import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styles from "./multi-line-widget.module.scss";
import type { WidgetRendererProps } from "../widget-renderer";
import ReactEChart from "echarts-for-react";
import { selectUnitsById } from "@src/reducers/units-slice";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { unitLabelOf } from "@src/cloud-connect/units/utils/unit-label-of";
import {
  isoToDurationMs,
  LINE_CHART_DEFAULT_DURATION_MS,
  resolvePollingMs,
} from "../constants";
import {
  buildMultiLineOption,
  MultiLineSeries,
  MULTI_LINE_DEFAULT_COLORS,
} from "./build-multi-line-option";
import { useThemeColorsForCharts } from "../use-theme-colors-for-charts";
import { NoDataInTimeframe } from "@src/components/atoms/no-data-in-timeframe/no-data-in-timeframe";
import Spinner from "@src/components/atoms/spinner/spinner";
import {
  OPCUAGaugeSemantics,
  QueryBinding,
  WidgetSemantics,
} from "@src/types/dashboards";
import { getQueryTimeseriesForNodeUseCase } from "@src/cloud-connect/use-cases/get-query-timeseries-for-node";
import { getActiveTimeseriesUseCase } from "@src/cloud-connect/timeseries/use-cases/get-active-timeseries-use-case";
import { getWidgetWindow } from "../widget-query";
import { useRefetchWhenVisible } from "@src/hooks/use-check-widget-in-viewport";
import {
  applyScaleToBounds,
  applyScaleToNumbers,
} from "../utils/value-scaling";
import { readAxisBounds } from "../axis-bounds";

const cssClassName = "multi-line-widget";

interface FetchedSeries {
  times: string[];
  values: (number | null)[];
  /** Aggregated-line bounds per slot, parallel to times/values. Multi-line
   *  is always aggregated so this is always populated in live mode; the
   *  renderer decides whether to draw sub-series on top based on
   *  semantics.show_min / semantics.show_max. */
  minValues?: (number | null)[];
  maxValues?: (number | null)[];
  nodeName: string;
  breadcrumbs: string;
  isLoading: boolean;
  /**
   * "ok" = fetch succeeded with at least one point. "empty" = fetch
   * succeeded but returned zero points (out-of-range window, healthy
   * source). "error" = the fetch itself failed (network, 4xx/5xx). The
   * "all error-or-empty → NoDataInTimeframe" gate uses this — we
   * previously conflated "error" and "empty" under one `hasNoData` flag
   * and lost the chip-color signal in the legend.
   */
  status: "ok" | "empty" | "error";
}

/**
 * Build a deterministic mock series per binding — same shape as the live
 * fetch. We use distinct mins/maxes per index so the lines don't overlap on
 * the preview tile.
 */
const buildMockSeries = (
  count: number,
  semantics: WidgetSemantics,
): MultiLineSeries[] => {
  const s = semantics as Partial<OPCUAGaugeSemantics>;
  const min = s.min_value ?? 0;
  const max = s.max_value ?? 100;
  const span = (max - min) * 0.08;
  const now = Date.now();
  const step = 10 * 60 * 1000; // 10 min
  const result: MultiLineSeries[] = [];
  for (let idx = 0; idx < count; idx++) {
    // Stagger each line's base by a fraction of the band so they look like
    // distinct series in the preview.
    const base =
      min +
      0.2 * (max - min) +
      (idx / Math.max(1, count - 1)) * 0.4 * (max - min);
    const times: string[] = [];
    const values: number[] = [];
    for (let i = 0; i < 8; i++) {
      times.push(new Date(now - (7 - i) * step).toISOString());
      values.push(Math.round((base + Math.sin(i + idx) * span) * 100) / 100);
    }
    result.push({
      name: `Series ${idx + 1}`,
      times,
      values,
      color: MULTI_LINE_DEFAULT_COLORS[idx],
    });
  }
  return result;
};

const getUnitLabel = (
  presentation: WidgetRendererProps["presentation"],
  units: ReturnType<typeof selectUnitsById>,
) => unitLabelOf(getUnitById(units, presentation?.unit));

/**
 * Resolves the per-binding color: prefer encoding.roles.value_X.color at the
 * same index, fall back to MULTI_LINE_DEFAULT_COLORS. The order in the
 * encoding-roles map is binding-id positional (value_0 = first line, etc).
 */
const colorForIndex = (
  index: number,
  encoding: WidgetRendererProps["encoding"],
): string | undefined => {
  const roles = encoding?.roles;
  if (!roles) return MULTI_LINE_DEFAULT_COLORS[index];
  // Roles live under `value_0`, `value_1`, ... — keyed by index, not lookup.
  const roleKey = Object.keys(roles).find((k) => k.endsWith(`_${index}`));
  const roleEntry = roleKey ? roles[roleKey] : undefined;
  if (Array.isArray(roleEntry)) return MULTI_LINE_DEFAULT_COLORS[index];
  return roleEntry?.color ?? MULTI_LINE_DEFAULT_COLORS[index];
};

/**
 * Fetches N timeseries in parallel — one per binding. We can't wrap
 * `useWidgetTimeseries` because the count of bindings varies (2–5) and
 * React hooks must be called in fixed order. So we run N parallel
 * `Promise.all` fetches keyed by `bindings.length + JSON.stringify` so
 * reorder-driven re-fetch fires reliably.
 */
interface UseMultiLineFetchResult {
  series: FetchedSeries[];
  isLoading: boolean;
  hasAnyData: boolean;
  hasAnyNoData: boolean;
}
const useMultiLineFetch = (
  bindings: QueryBinding[],
  opts: {
    from?: string;
    to?: string;
    durationMs?: number | null;
    frequency: string;
    preview: boolean;
    inViewport?: boolean;
  },
): UseMultiLineFetchResult => {
  const { inViewport = true } = opts;
  const [series, setSeries] = useState<FetchedSeries[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Visibility bridge — same role as use-widget-timeseries.
  const latestLoadRef = useRef<(() => void) | null>(null);
  const inViewportRef = useRefetchWhenVisible(inViewport, latestLoadRef);

  // Stable dep — JSON.stringify the bindings. Cheap (≤ 5 entries) and lets
  // useEffect skip when nothing actually changed (e.g. parent re-render).
  const bindingsKey = useMemo(
    () =>
      JSON.stringify(
        bindings.map((b) => ({
          source: b.source ?? "",
          address: b.address,
        })),
      ),
    [bindings],
  );

  useEffect(() => {
    if (opts.preview || bindings.length === 0) {
      latestLoadRef.current = null;
      setSeries([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;

    // Resolve the request window PER TICK: a relative (duration) window must
    // roll forward on every refresh instead of staying pinned to mount time.
    // Explicit absolute endpoints (from/to) stay put.
    const windowFor = (): { fromIso: string; toIso: string } => {
      // One clock read per load so both endpoints describe the same instant.
      const now = Date.now();
      return {
        fromIso:
          opts.durationMs != null
            ? new Date(now - opts.durationMs).toISOString()
            : opts.from ??
              new Date(now - LINE_CHART_DEFAULT_DURATION_MS).toISOString(),
        toIso: opts.to ?? new Date(now).toISOString(),
      };
    };

    setIsLoading(true);

    const fetchOne = async (
      b: QueryBinding,
      fromIso: string,
      toIso: string,
    ): Promise<FetchedSeries> => {
      const machine = b.source ?? "";
      const node = b.address;
      // Ponytail: default status is "empty" — the bound input is present
      // but no points came back. The "error" branch below flips this when
      // the fetch itself fails. A binding with no source/address stays
      // here (a degenerate case — multi-line shouldn't render rows that
      // haven't finished filling in).
      const empty: FetchedSeries = {
        times: [],
        values: [],
        nodeName: node,
        breadcrumbs: "",
        isLoading: false,
        status: "empty",
      };
      if (!machine || !node) return empty;

      const [seriesResult, activeResult] = await Promise.all([
        getQueryTimeseriesForNodeUseCase({
          group: machine,
          source: node,
          frequency: opts.frequency as
            | "raw"
            | "quarterhourly"
            | "hourly"
            | "daily"
            | "weekly"
            | "monthly"
            | "yearly",
          from: fromIso,
          to: toIso,
        }),
        getActiveTimeseriesUseCase(machine),
      ]);

      const out: FetchedSeries = { ...empty };
      // Two failure flavours from the /api/v3 tsdata endpoint:
      //   - `!success` (axios/network/HTTP error path): we mark "error"
      //     so the chip turns alarmRed.
      //   - `success === true && (no aggregated_values AND no values)`:
      //     healthy response with zero points — "empty".
      // Otherwise: data → "ok".
      if (!seriesResult.success) {
        out.status = "error";
      } else if (seriesResult.data) {
        // The /api/v3 tsdata endpoint returns one of two shapes:
        //   - raw (frequency="raw"):
        //       { values: [{ time, value }, ...] }
        //   - aggregated (hourly|quarterhourly|daily|...):
        //       { aggregated_values: [{ time, v_min, v_avg, v_max, v_count }, ...] }
        // The previous version only read `values`, so aggregated responses
        // came back as empty arrays and the chart showed "no data" even
        // though the API had returned points. The multi-line widget is
        // always aggregated by spec, so `aggregated_values` is the path
        // that actually fires at runtime; we keep the raw branch too as a
        // safety net in case future code calls with "raw".
        // v_avg is the standard aggregate metric for a line chart
        // (central tendency per bucket); v_min/v_max populate the per-row
        // sub-series that the renderer overlays when semantics.show_min /
        // semantics.show_max are true.
        const data = seriesResult.data as {
          values?: { time: string; value: number | string | null }[];
          aggregated_values?: {
            time: string;
            v_min: number | string | null;
            v_avg: number | string | null;
            v_max: number | string | null;
            v_count: number | string | null;
          }[];
        };
        const aggregated = data.aggregated_values;
        const raw = data.values;
        if (aggregated && aggregated.length > 0) {
          out.times = aggregated.map((p) => p.time);
          out.values = aggregated.map((p) =>
            typeof p.v_avg === "number" ? p.v_avg : null,
          );
          // v_min / v_max come from the same per-bucket point — line up
          // 1:1 with times/values so the renderer can overlay without
          // zipping. null entries pass through (off-the-chart buckets).
          out.minValues = aggregated.map((p) =>
            typeof p.v_min === "number" ? p.v_min : null,
          );
          out.maxValues = aggregated.map((p) =>
            typeof p.v_max === "number" ? p.v_max : null,
          );
          out.status = "ok";
        } else if (raw && raw.length > 0) {
          out.times = raw.map((p) => p.time);
          out.values = raw.map((p) =>
            typeof p.value === "number" ? p.value : null,
          );
          // raw path: no aggregate bounds exist. Don't leave stale bounds
          // from a previous aggregated call in state — they'd be drawn
          // over a raw line that has different timestamps.
          out.minValues = undefined;
          out.maxValues = undefined;
          out.status = "ok";
        } else {
          // Healthy response, zero points — proceed as "empty" (yellow).
        }
      } else {
        // success === true but data === undefined: keep "empty" default;
        // the chip will still go yellow when nothing else overwrites it.
      }
      if (activeResult.success && activeResult.data) {
        const entry = Object.values(activeResult.data.timeseries).find(
          (t) => t.source === node,
        );
        if (entry) {
          out.nodeName = entry.name ?? node;
          // `node_path` and its `parent_names` are typed as required but
          // the active-timeseries endpoint returns null/undefined for
          // top-level nodes. The previous version crashed with
          // "entry.node_path is null" when one of the bindings was a
          // root-level source. Optional-chain + nullish coalesce keeps
          // the breadcrumb label empty for these cases.
          out.breadcrumbs = entry.node_path?.parent_names?.join("/") ?? "";
        }
      }
      return out;
    };

    // Overlap guard: slow endpoints must never stack parallel fetches when
    // the interval fires again mid-request.
    let inFlight = false;
    // Only the first (effect-driven) load shows the spinner; background
    // refreshes keep the chart mounted (echarts wouldn't survive a mount
    // cycle per tick — legend/zoom state would reset).
    let isInitialLoad = true;
    const load = () => {
      if (inFlight || document.hidden || !inViewportRef.current) return;
      inFlight = true;
      if (isInitialLoad) {
        isInitialLoad = false;
        setIsLoading(true);
      }
      const { fromIso, toIso } = windowFor();
      Promise.all(bindings.map((b) => fetchOne(b, fromIso, toIso)))
        .then((results) => {
          if (cancelled) return;
          setSeries(results);
        })
        .catch(() => {
          // A rejected binding must never strand the guard — otherwise the
          // overlap check blocks every later tick and polling goes dead.
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
            inFlight = false;
          }
        });
    };

    // Expose the latest fetch to the visibility bridge (re-entry refetch).
    latestLoadRef.current = load;
    load();
    // Multi-line is always aggregated: refresh at the bucket cadence so each
    // new closed bucket is picked up, never faster.
    const pollingMs = resolvePollingMs(opts.frequency, null);
    const intervalId =
      pollingMs != null ? setInterval(load, pollingMs) : undefined;
    return () => {
      cancelled = true;
      if (intervalId != null) clearInterval(intervalId);
      latestLoadRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bindingsKey,
    opts.from,
    opts.to,
    opts.durationMs,
    opts.frequency,
    opts.preview,
  ]);

  const hasAnyData = series.some((s) => s.times.length > 0);
  // "All error-or-empty" gate — drives the NoDataInTimeframe fallback
  // when EVERY binding is unhealthy (i.e. nothing usable to draw).
  // Distinct from `hasAnyData` (ANY row has points) → renders the chart
  // even if some rows are red/yellow.
  const hasAnyNoData =
    series.length > 0 && series.every((s) => s.status !== "ok");

  return { series, isLoading, hasAnyData, hasAnyNoData };
};

const ResizeAwareChart = ({
  option,
  unitLabel,
  hasBoundsSubSeries = false,
}: {
  option: any;
  /** Persistent unit label rendered top-right (unit id resolved via the slice). */
  unitLabel?: string;
  /**
   * When true, attach a `legendselectchanged` handler that keeps each
   * row's `(min)` / `(max)` sub-series in lock-step with its avg entry.
   * With bounds hidden from the legend block (see buildMultiLineOption),
   * only the avg chip is clickable; this handler mirrors the user's
   * selection onto the suffixed sub-series so the dashed bounds fade
   * together with the solid avg — one chip per node, three synced lines.
   * Skipped when the chart has no bound sub-series to link (dead code).
   */
  hasBoundsSubSeries?: boolean;
}) => {
  const echartsRef = useRef<ReactEChart>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const resize = () =>
      echartsRef.current
        ?.getEchartsInstance()
        ?.resize({ width: "auto", height: "auto" });
    let lastW = -1;
    let lastH = -1;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0]?.contentRect ?? {
        width: 0,
        height: 0,
      };
      if (width === lastW && height === lastH) return; // no real change
      lastW = width;
      lastH = height;
      // Defer to the next frame: resizing from inside the RO callback
      // re-flows the observed element and trips the browser's "ResizeObserver
      // loop completed with undelivered notifications" warning.
      requestAnimationFrame(resize);
    });
    observer.observe(el);
    // Observes the closest .grid-stack-item ancestor, because when expanding and collapsing
    // the left and right columns charts should rerender to not be cropped.
    const tileEl = el.closest(".grid-stack-item");
    if (tileEl && tileEl !== el) observer.observe(tileEl);
    // One RAF after paint with the layout settled: covers echarts initializing
    // before the container had its final width (live charts mount after the
    // fetch/spinner, so init can land mid-layout while the RO callback timing
    // is less reliable than the sync mock mount).
    const raf = requestAnimationFrame(resize);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  // Per-row legend linkage for the bounds sub-series. When the user clicks
  // a node's avg legend chip, echarts fires `legendselectchanged` with a
  // `selected` dict mapping every series name to its bool state. We mirror
  // that state onto the matching ` (min)` / ` (max)` slots so the dashed
  // bounds fade in/out together with the solid avg. We never touch the
  // avg chip's state — the dict comes pre-set by echarts from the click.
  useEffect(() => {
    if (!hasBoundsSubSeries) return;
    const chart = echartsRef.current?.getEchartsInstance();
    if (!chart) return;
    const handler = (params: { selected?: Record<string, boolean> }) => {
      if (!params.selected) return;
      const next: Record<string, boolean> = { ...params.selected };
      for (const [name, isSelected] of Object.entries(params.selected)) {
        // Skip the suffixed entries themselves — they're derived, not
        // clicked. Their next-value comes from the matching avg row.
        if (name.endsWith(" (min)") || name.endsWith(" (max)")) continue;
        next[`${name} (min)`] = isSelected;
        next[`${name} (max)`] = isSelected;
      }
      // setOption on the same path echarts used for the click avoids
      // dispatching a no-op action; the chart applies it on next frame.
      chart.setOption({ legend: { selected: next } });
    };
    chart.on("legendselectchanged", handler);
    return () => {
      chart.off("legendselectchanged", handler);
    };
  }, [hasBoundsSubSeries]);

  return (
    <div ref={wrapperRef} className={styles[`${cssClassName}__chart`]}>
      {unitLabel ? (
        <span className={styles[`${cssClassName}__unit`]}>{unitLabel}</span>
      ) : null}
      <ReactEChart
        ref={echartsRef}
        style={{ width: "100%", height: "100%" }}
        option={option}
      />
    </div>
  );
};

export const MultiLineWidget = ({
  title,
  encoding,
  semantics,
  presentation,
  query,
  preview = false,
  showTitle = true,
  inViewport = true,
}: WidgetRendererProps) => {
  const s = semantics as Partial<
    OPCUAGaugeSemantics & { thresholds?: unknown }
  >;
  const thresholds = s.thresholds as
    | { warning?: number | null; critical?: number | null }
    | null
    | undefined;

  const units = useSelector(selectUnitsById);
  const unitLabel = getUnitLabel(presentation, units);

  const bindings = query?.bindings ?? [];
  const frequency = query?.sampling?.frequency ?? "hourly";
  const window = getWidgetWindow(query);

  const {
    series: fetchedSeries,
    isLoading,
    hasAnyData,
    hasAnyNoData,
  } = useMultiLineFetch(bindings, {
    frequency,
    durationMs: isoToDurationMs(window?.duration),
    from: window?.from,
    to: window?.to,
    preview,
    inViewport,
  });

  // Multi-line is always aggregated by spec, so show_min/show_max apply
  // unconditionally. Default OFF — the per-row checkboxes in the drawer
  // flip these flags before save. The option builder uses them to overlay
  // v_min / v_max sub-series on top of the v_avg lines.
  const semForBounds = semantics as Partial<
    OPCUAGaugeSemantics & { show_min?: boolean; show_max?: boolean }
  >;
  const showMin = semForBounds.show_min === true;
  const showMax = semForBounds.show_max === true;

  // Mock + static: picker preview AND freshly-added widgets (no query
  // binding yet — templates are unbound until configured). Mock count =
  // binding count when present, otherwise 2 (the "user has just clicked
  // the picker tile" preview).
  const showMock = preview || bindings.length === 0;
  const mockCount = bindings.length > 0 ? bindings.length : 2;
  const mockSeries = useMemo(
    () => (showMock ? buildMockSeries(mockCount, semantics) : []),
    [showMock, mockCount, semantics],
  );

  // Resolved theme colours for chart text — see
  // `useThemeColorsForCharts`. Drives a rebuild when the picker
  // drag-stops mutate the central-panel CSS vars, so a live theme
  // change re-builds the echarts options and the canvas repaints
  // without waiting for the next data tick.
  // `preview` = picker tile: fixed dark chrome, so no dashboard palette.
  const themeColors = useThemeColorsForCharts(preview);

  // Compile the e-charts option. In live mode, swap mockSeries for the
  // freshly fetched ones (each binding keeps its position). Live rows
  // also carry minValues/maxValues so the renderer can overlay v_min /
  // v_max sub-series when the corresponding semantics flags are on. The
  // per-row `status` propagates into the legend chip color (alarmRed for
  // fetch errors, amber-yellow for empty responses) so the user can see
  // at a glance which bindings are unhealthy without hovering.
  const option = useMemo(() => {
    // Manual Y bounds (if the user set a valid pair) override the auto axis.
    const axis = readAxisBounds(semantics);
    const inputSeries: MultiLineSeries[] = showMock
      ? mockSeries
      : fetchedSeries.map((s, idx) => {
          const field = `value_${idx}`;
          // Scale each series by its `value_N` transform before the chart
          // option is built; the fetched data stays raw. Bounds go through
          // `applyScaleToBounds` so a negative factor keeps min ≤ max.
          const bounds = applyScaleToBounds(
            field,
            s.minValues,
            s.maxValues,
            encoding?.transforms,
          );
          return {
            name: s.nodeName || `Series ${idx + 1}`,
            times: s.times,
            values: applyScaleToNumbers(field, s.values, encoding?.transforms),
            minValues: bounds.minValues,
            maxValues: bounds.maxValues,
            breadcrumbs: s.breadcrumbs,
            color: colorForIndex(idx, encoding),
            status: s.status,
          };
        });
    return buildMultiLineOption({
      series: inputSeries,
      unitLabel,
      thresholds,
      showMin,
      showMax,
      yMin: axis?.min ?? null,
      yMax: axis?.max ?? null,
      static: showMock,
      themeColors,
    });
  }, [
    showMock,
    mockSeries,
    fetchedSeries,
    encoding,
    unitLabel,
    thresholds,
    semantics,
    showMin,
    showMax,
    themeColors,
  ]);

  const body = showMock ? (
    <ResizeAwareChart
      option={option}
      unitLabel={unitLabel}
      hasBoundsSubSeries={showMin || showMax}
    />
  ) : isLoading ? (
    <div className={styles[`${cssClassName}__chart`]}>
      <Spinner />
    </div>
  ) : !hasAnyData || hasAnyNoData ? (
    <NoDataInTimeframe />
  ) : (
    <ResizeAwareChart
      option={option}
      unitLabel={unitLabel}
      hasBoundsSubSeries={showMin || showMax}
    />
  );

  return (
    <div className={styles[cssClassName]}>
      {showTitle && (
        <span className={styles[`${cssClassName}__title`]}>
          {title || "Multi-line Chart"}
        </span>
      )}
      {body}
    </div>
  );
};
